/**
 * Pure state machine for an assistant onboarding — a guided kickoff a page
 * hands to the assistant instead of showing a form (`AssistantHost.tsx`
 * drives this against the live chat). No React, no DOM: safe to unit test.
 *
 * States, keyed by whether an onboarding is running and whether its kickoff
 * message has been sent yet:
 *  - `null` — no onboarding running.
 *  - `{ id, params, conversationId, kickoffSent: false }` — a fresh chat was
 *    minted for this onboarding and the hidden kickoff turn hasn't gone out.
 *  - `{ ..., kickoffSent: true }` — the kickoff has been sent; now waiting
 *    for the finish capability.
 *
 * There is no "reopen and restore" case: the captive-takeover choreography
 * (`AssistantHost.tsx`'s `TakeoverPhase`, below) fully unwinds back to `null`
 * on finish, close, or new-chat alike — a conversation an onboarding ran in
 * is, from then on, just an ordinary thread.
 */

import { THREAD_KICKOFF_MARKER_KIND, type AssistantOnboardingId } from '@chatsdet/types';

export type OnboardingState = {
  id: AssistantOnboardingId;
  params: Record<string, string> | undefined;
  conversationId: string;
  kickoffSent: boolean;
};

/** Start a fresh onboarding against a newly minted conversation id. */
export function startOnboarding(
  id: AssistantOnboardingId,
  params: Record<string, string> | undefined,
  conversationId: string
): OnboardingState {
  return { id, params, conversationId, kickoffSent: false };
}

/**
 * The onboarding (or plain thread kickoff, below) is ready for its kickoff to
 * be sent once the chat has caught up to the conversation it was started
 * against and finished preparing (conversation ensured, message history
 * loaded). Typed structurally so it works for both `OnboardingState` and
 * `KickoffState` — anything carrying a `conversationId` and `kickoffSent`.
 */
export function shouldSendKickoff(
  state: { conversationId: string; kickoffSent: boolean } | null,
  currentConversationId: string | null,
  isPreparing: boolean
): boolean {
  return (
    state !== null &&
    !state.kickoffSent &&
    state.conversationId === currentConversationId &&
    !isPreparing
  );
}

/** Generic over any state shape carrying `kickoffSent` — `OnboardingState` and `KickoffState` alike. */
export function markKickoffSent<T extends { kickoffSent: boolean }>(state: T): T {
  return { ...state, kickoffSent: true };
}

// -----------------------------------------------------------------------------
// Plain "thread with kickoff" — a page opens the ordinary assistant panel
// (no captive takeover) and pre-seeds it with a hidden first turn. Shares
// `shouldSendKickoff`/`markKickoffSent` with the onboarding machine above,
// since both are just "a hidden kickoff turn sent once a freshly minted
// conversation is ready".
// -----------------------------------------------------------------------------

/** A page's request to open a thread and send a hidden first turn into it. */
export type KickoffRequest = {
  /** The hidden user turn sent to open the thread. */
  text: string;
  /** Shown on the marker divider in place of the raw kickoff text. */
  label: string;
  /** Merged into `pageContext.params` for the lifetime of this kickoff (mirrors onboarding params). */
  params: Record<string, string>;
  /** Extra fields merged into the kickoff message's `metadata`, alongside `kind` and `label`. */
  metadata: Record<string, unknown>;
};

export type KickoffState = KickoffRequest & {
  conversationId: string;
  kickoffSent: boolean;
};

/** Start a fresh thread kickoff against a newly minted conversation id. */
export function startKickoff(request: KickoffRequest, conversationId: string): KickoffState {
  return { ...request, conversationId, kickoffSent: false };
}

/** The `metadata` payload for a thread kickoff's hidden user turn. */
export function kickoffMetadata(state: KickoffState): Record<string, unknown> {
  return { kind: THREAD_KICKOFF_MARKER_KIND, label: state.label, ...state.metadata };
}

// -----------------------------------------------------------------------------
// Plain "ask" — a page sends `text` as an ordinary VISIBLE user turn into a
// FRESH thread (contrast the hidden kickoff above, which renders as a marker
// divider). Shares `shouldSendKickoff`/`markKickoffSent` since both are just
// "a turn sent once a newly minted conversation is ready" — only the
// metadata (none, here — `{}`, so nothing marks it as a kickoff) and
// visibility differ.
// -----------------------------------------------------------------------------

export type AskState = {
  text: string;
  /** Merged into `pageContext.params` for the lifetime of this ask's thread (mirrors `KickoffRequest.params`). */
  params: Record<string, string>;
  conversationId: string;
  kickoffSent: boolean;
};

/** Start a fresh "ask" against a newly minted conversation id. */
export function startAsk(
  text: string,
  params: Record<string, string>,
  conversationId: string
): AskState {
  return { text, params, conversationId, kickoffSent: false };
}

/**
 * Does a `capabilities` tool result finish the given onboarding? Only the
 * onboarding's own terminal capability, and only a genuine success — an
 * execution failure (`ok: false`) must leave the onboarding running so the
 * assistant can retry. Takes just the `id` (not the full `OnboardingState`)
 * since that's all it needs — `useAssistantChat.ts`'s own onboarding record
 * (`ActiveOnboarding`) is a subset of `OnboardingState` and satisfies this
 * structurally.
 */
export function matchesFinish(
  state: { id: AssistantOnboardingId } | null,
  finishCapabilityId: string,
  output: unknown
): output is { ok: true; capabilityId: string; result: unknown } {
  if (state === null) return false;
  if (typeof output !== 'object' || output === null) return false;
  const record = output as { ok?: unknown; capabilityId?: unknown };
  return record.ok === true && record.capabilityId === finishCapabilityId;
}

// -----------------------------------------------------------------------------
// Takeover phase machine
// -----------------------------------------------------------------------------

/**
 * The captive-takeover choreography's phase, driven by `AssistantHost.tsx`:
 *
 *   idle → flying → expanding → open → finishing → collapsing → returning → idle
 *                       ↑____________cancel____________↓
 *
 * `idle`: no onboarding running (or its takeover has fully returned).
 * `flying`: the launcher puck is animating from its corner to the center of
 *   the content area.
 * `expanding`: the puck has arrived; the takeover surface is growing from a
 *   puck-sized circle at that center point to the full content-area rect.
 * `open`: fully expanded — the conversation is running.
 * `finishing`: the finish capability succeeded — holding on the assistant's
 *   own "done" line for a beat before the surface starts to move (see
 *   `holdComplete` below). Skipped entirely for a user-initiated exit.
 * `collapsing`: the surface is shrinking back to the center circle (after the
 *   finishing hold, or immediately on cancel/close/new-chat).
 * `returning`: the circle has fully collapsed; the puck is flying back to
 *   its pinned corner.
 *
 * `cancel` is the user-initiated exit (Escape, clicking the puck, closing,
 * starting a new chat) and is honored from every phase between `flying` and
 * `finishing` inclusive — it always wins over the in-flight choreography,
 * which is why `nextTakeoverPhase` retargets rather than snapping (framer's
 * `animate()` interpolates from a motion value's CURRENT position, not from
 * whatever the last target was).
 */
export type TakeoverPhase =
  | 'idle'
  | 'flying'
  | 'expanding'
  | 'open'
  | 'finishing'
  | 'collapsing'
  | 'returning';

/** Events that move the phase machine forward. Any event not valid for the current phase is a no-op. */
export type TakeoverEvent =
  /** A page requested an onboarding — starts the flight. */
  | { type: 'start' }
  /** The puck reached the content area's center. */
  | { type: 'arrived' }
  /** The surface finished growing to the full rect. */
  | { type: 'expanded' }
  /** The finish capability succeeded — enters the pre-collapse hold beat. */
  | { type: 'finish' }
  /** The hold beat elapsed (streaming settled or the cap ran out, plus the hold itself). */
  | { type: 'holdComplete' }
  /** The user ended the takeover directly — Escape, puck click, close, new chat. */
  | { type: 'cancel' }
  /** The surface finished shrinking back to the circle. */
  | { type: 'collapsed' }
  /** The puck finished flying back to its corner. */
  | { type: 'returned' };

const TAKEOVER_TRANSITIONS: Record<TakeoverPhase, Partial<Record<TakeoverEvent['type'], TakeoverPhase>>> = {
  idle: { start: 'flying' },
  // No surface exists yet during flight — cancelling just aborts the flight
  // (the puck reverses toward its corner; see `AssistantHost.tsx`), no
  // collapse beat needed.
  flying: { arrived: 'expanding', cancel: 'idle' },
  expanding: { expanded: 'open', finish: 'finishing', cancel: 'collapsing' },
  open: { finish: 'finishing', cancel: 'collapsing' },
  finishing: { holdComplete: 'collapsing', cancel: 'collapsing' },
  collapsing: { collapsed: 'returning' },
  returning: { returned: 'idle' },
};

/** The next phase for `event`, or `phase` unchanged if that event doesn't apply to it. */
export function nextTakeoverPhase(phase: TakeoverPhase, event: TakeoverEvent): TakeoverPhase {
  return TAKEOVER_TRANSITIONS[phase][event.type] ?? phase;
}
